/// Maps the name of the Multistep tool that was removed from toolbox
/// to last time the tool was removed.
export type Tombstones = Record<string, string>;

export function emptyTombstones(): Tombstones {
    return {};
}

export interface ToolTraits<Tool> {
    /// return a key that uniquely identifies the tool within the toolbox.
    getKey(tool: Tool): string;
    /// return a string to uniquely identify a tombstone.
    /// tools that can never be dropped from the toolbox return null.
    getEpitaph(tool: Tool): string | null;
    /// when was the tool last modified?
    getTimestamp(tool: Tool): string;
}

export interface MergeResult<Tool> {
    tools: Tool[];
    tombstones: Tombstones;
    orderChanged: boolean;
}

/// Reorder baseTools according to tools, and add new tools to toolbox.
/// Remove tools from toolbox that are listed in trash.
/// The order of baseTools is abandoned and the order of tools dominates.
export function mergeToolbox<Tool>(
    baseTools: Tool[], tombstones: Tombstones,
    tools: Tool[], trash: Tombstones, traits: ToolTraits<Tool>): MergeResult<Tool> 
{
    // Copy tools from trash into tombstones.
    tombstones = {...tombstones};
    for (const [name, timestamp] of Object.entries(trash)) {
        if (!(tombstones[name] >= timestamp)) {
            tombstones[name] = timestamp;
        }
    }

    // Collect the set of existing tools, dropping tools for which we find
    // fresh tombstones.
    const existingTools = new Map<string, Tool>();
    for (const tool of baseTools) {
        const key = traits.getKey(tool);
        const epitaph = traits.getEpitaph(tool);
        if (epitaph  === null) {
            // Ops are never dropped.
            existingTools.set(key, tool);
        } else if (tombstones[epitaph] >= traits.getTimestamp(tool)) {
            // Tool was deleted.
        } else {
            existingTools.set(key, tool);
        }
    }

    // Collect the set of tools to keep in the toolbox.
    const keepers: Tool[] = [];
    for (const tool of tools) {
        const key = traits.getKey(tool);
        const existingTool = existingTools.get(key);
        if (existingTool) {
            // Keep the fresher of the tools with the same name.
            const keeper = traits.getTimestamp(existingTool)
                >= traits.getTimestamp(tool)
                ? existingTool : tool;
            keepers.push(keeper);
            existingTools.delete(key);
        } else {
            keepers.push(tool);
        }
    }

    // Keep left over existing tools.
    keepers.push(...existingTools.values());

    // If we're keeping the a tool, then by definition it's not dead, so
    // make sure there isn't a tombstone with the same name.
    for (const keeper of keepers) {
        const epitaph = traits.getEpitaph(keeper);
        if (epitaph  !== null) {
            delete tombstones[epitaph];
        }
    }

    // Has the order of tools changed?
    const sig1 = JSON.stringify(baseTools.map(tool => traits.getKey(tool)));
    const sig2 = JSON.stringify(keepers.map(tool => traits.getKey(tool)));
    return {
        orderChanged: sig1 !== sig2,
        tombstones,
        tools: keepers
    };
}
